// SPDX-License-Identifier: GPL-3.0-or-later
// The featured turn of this page: one stream at a time, over one or more sockets.
// The page owns the game and the dialogs. This module owns the stream rules, so a test can run them:
// - Each new stream starts its `seq` at max(last seq + 1, the wall clock in ms). The Worker keeps the
//   last `seq` of a live turn and rejects every frame that does not rise. A reload loses the last
//   `seq`, and the clock base is always above the old one (a stream sends 8 frames a second at most).
// - A game that ends while the stream is lost or connecting sends its game_over frame on the new stream.
// - While a socket is connecting, a free game must not start (it would restart the featured game).

import type { EngineEvent, FeaturedServerMessage, GameStatus } from "../src/shared/contracts.ts";
import { type FeaturedStream, openFeaturedStream, type SocketLike } from "./featured-stream.ts";

/** The state of the stream of this page. */
export type StreamPhase = "none" | "connecting" | "live" | "lost";

type EndMessage = Extract<FeaturedServerMessage, { type: "end" }>;
type ErrorMessage = Extract<FeaturedServerMessage, { type: "error" }>;

/** The `seq` of the first frame of a new stream. It is above `lastSeq` and at least the clock. */
export function firstSeqFor(lastSeq: number, nowMs: number): number {
  const clock = Number.isFinite(nowMs) ? Math.floor(nowMs) : 0;
  return Math.max(lastSeq + 1, clock);
}

/** What the page tells the session. */
export interface SessionDeps {
  /** Open the socket. A browser that cannot open it gives null. */
  readonly openSocket: () => SocketLike | null;
  /** The wall clock in ms. The page passes `Date.now`. */
  readonly now: () => number;
  /** The phase changed. */
  readonly onPhase: (phase: StreamPhase) => void;
  /** The Worker accepted the ticket. `resume` is true for a reconnect of a running game. */
  readonly onReady: (resume: boolean) => void;
  /** The Worker ended the turn. */
  readonly onEnd: (msg: EndMessage) => void;
  /** The Worker sent an error frame. */
  readonly onError: (msg: ErrorMessage) => void;
}

/** The session. */
export interface TurnSession {
  readonly phase: StreamPhase;
  /** The last `seq` that went out on any stream of this session. */
  readonly lastSeq: number;
  /** Open a stream. False when the socket could not open. */
  connect(ticket: string, resume: boolean): boolean;
  /** True while a socket connects. A free game must not start then. */
  blocksFreeStart(): boolean;
  /** Pass an engine event. A game_over event sends the final frame. */
  onEngineEvent(ev: EngineEvent, gameMs: number): void;
  /** The game ended by the player (End game). Same as a game_over event with this score. */
  gameOver(finalScore: number, gameMs: number): void;
  /**
   * Send what is due. Call it for each frame of the page loop. While the game is paused, pass
   * `paused` = true: the stream then sends the same status every PAUSED_HEARTBEAT_MS, so the
   * Worker keeps the turn alive.
   */
  pump(nowMs: number, status: GameStatus, paused?: boolean): void;
}

/** The state of one session. The functions below change it. */
interface Core {
  readonly deps: SessionDeps;
  phase: StreamPhase;
  stream: FeaturedStream | null;
  lastSeq: number;
  /** True after the final frame was handed to a live stream. Later events are dropped. */
  finalGiven: boolean;
  /** A game over that waits for a live stream (the stream was lost or is connecting). */
  pendingFinal: { score: number; gameMs: number } | null;
}

function setPhase(core: Core, next: StreamPhase): void {
  core.phase = next;
  core.deps.onPhase(next);
}

function keepSeq(core: Core): void {
  core.lastSeq = Math.max(core.lastSeq, core.stream?.lastSeq ?? 0);
}

/** The game ended: hand the final score to a live stream, or keep it for the next stream. */
function giveFinal(core: Core, score: number, gameMs: number): void {
  if (core.finalGiven) return;
  if (core.stream !== null && core.phase === "live") {
    core.stream.finish(score, gameMs);
    core.finalGiven = true;
  } else if (core.phase === "lost" || core.phase === "connecting") {
    core.pendingFinal = { score, gameMs };
  }
}

/** The Worker accepted the ticket. A final score that waited goes out now. */
function onStreamReady(core: Core, resume: boolean): void {
  setPhase(core, "live");
  if (core.pendingFinal !== null) {
    const waiting = core.pendingFinal;
    core.pendingFinal = null;
    giveFinal(core, waiting.score, waiting.gameMs);
  }
  core.deps.onReady(resume);
}

function onStreamEnd(core: Core, msg: Extract<FeaturedServerMessage, { type: "end" }>): void {
  keepSeq(core);
  core.stream = null;
  core.pendingFinal = null;
  setPhase(core, "none");
  core.deps.onEnd(msg);
}

function onStreamLost(core: Core): void {
  keepSeq(core);
  core.stream = null;
  setPhase(core, "lost");
}

/** Open a stream. False when the socket could not open. */
function connectStream(core: Core, ticket: string, resume: boolean): boolean {
  const socket = core.deps.openSocket();
  if (socket === null) {
    setPhase(core, "none");
    return false;
  }
  core.stream?.close();
  keepSeq(core);
  core.finalGiven = false;
  if (!resume) core.pendingFinal = null;
  setPhase(core, "connecting");
  const first = firstSeqFor(core.lastSeq, core.deps.now());
  core.stream = openFeaturedStream(
    socket,
    ticket,
    {
      onReady: () => onStreamReady(core, resume),
      onAck: () => {
        // The Worker keeps its own score. Nothing to show.
      },
      onEnd: (msg) => onStreamEnd(core, msg),
      onError: (msg) => core.deps.onError(msg),
      onLost: () => onStreamLost(core),
    },
    { startSeq: first - 1 },
  );
  return true;
}

/** Make the session. */
export function createTurnSession(deps: SessionDeps): TurnSession {
  const core: Core = {
    deps,
    phase: "none",
    stream: null,
    lastSeq: 0,
    finalGiven: false,
    pendingFinal: null,
  };
  return {
    get phase() {
      return core.phase;
    },
    get lastSeq() {
      return Math.max(core.lastSeq, core.stream?.lastSeq ?? 0);
    },
    connect: (ticket, resume) => connectStream(core, ticket, resume),
    blocksFreeStart: () => core.phase === "connecting",
    onEngineEvent(ev, gameMs) {
      if (ev.type === "game_over") {
        giveFinal(core, ev.finalScore, gameMs);
        return;
      }
      if (core.stream !== null && core.phase === "live" && !core.finalGiven) {
        core.stream.queueEvent(ev, gameMs);
      }
    },
    gameOver: (score, gameMs) => giveFinal(core, score, gameMs),
    pump(nowMs, status, paused = false) {
      core.stream?.pump(nowMs, status, paused);
    },
  };
}
